import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { Button } from '../../ui/button';
import { useToast } from '../../../hooks/use-toast';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CURRENCIES = [
  'USD', 'EUR', 'GBP', 'AED', 'SAR', 'JPY', 'CNY', 'INR', 'CHF', 'CAD', 'AUD', 'BRL'
];

const BankAccountFormModal = ({ isOpen, onClose, companyId, account = null, onSuccess }) => {
  const [formData, setFormData] = useState({
    bank_name: '',
    account_name: '',
    account_number: '',
    iban: '',
    swift: '',
    routing: '',
    currency: 'USD',
    is_default: false
  });
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  const token = localStorage.getItem('admin_token');
  const isEdit = !!account;

  useEffect(() => {
    if (account) {
      setFormData({
        bank_name: account.bank_name || '',
        account_name: account.account_name || '',
        account_number: account.account_number || '',
        iban: account.iban || '',
        swift: account.swift || '',
        routing: account.routing || '',
        currency: account.currency || 'USD',
        is_default: account.is_default || false
      });
    } else {
      // Reset for new account
      setFormData({
        bank_name: '',
        account_name: '',
        account_number: '',
        iban: '',
        swift: '',
        routing: '',
        currency: 'USD',
        is_default: false
      });
    }
  }, [account]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const payload = { ...formData };
      
      // Remove empty optional fields
      Object.keys(payload).forEach(key => {
        if (payload[key] === '' && key !== 'is_default') {
          delete payload[key];
        }
      });

      let url, method;
      
      if (isEdit) {
        url = `${API_URL}/api/procurement/bank-accounts/${account.id}`;
        method = 'PUT';
      } else {
        url = `${API_URL}/api/procurement/bank-accounts?company_id=${companyId}`;
        method = 'POST';
      }

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to save bank account');
      }

      toast({
        title: 'Success',
        description: `Bank account ${isEdit ? 'updated' : 'created'} successfully`
      });

      onSuccess();
    } catch (error) {
      console.error('Error saving bank account:', error);
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <h2 className="text-xl font-bold text-[#2B2D42]">
            {isEdit ? 'Edit Bank Account' : 'Add Bank Account'}
          </h2>
          <button onClick={onClose} className="text-[#6C757D] hover:text-[#2B2D42]">
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Bank Name */}
          <div>
            <label className="block text-sm font-medium text-[#2B2D42] mb-2">
              Bank Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="bank_name"
              value={formData.bank_name}
              onChange={handleChange}
              required
              placeholder="e.g., Bank of America, HSBC, Emirates NBD"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00B4D8]"
            />
          </div>

          {/* Account Name */}
          <div>
            <label className="block text-sm font-medium text-[#2B2D42] mb-2">
              Account Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="account_name"
              value={formData.account_name}
              onChange={handleChange}
              required
              placeholder="Account holder name"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00B4D8]"
            />
          </div>

          {/* Account Number */}
          <div>
            <label className="block text-sm font-medium text-[#2B2D42] mb-2">
              Account Number <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="account_number"
              value={formData.account_number}
              onChange={handleChange}
              required
              placeholder="Account number"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00B4D8] font-mono"
            />
          </div>

          {/* IBAN */}
          <div>
            <label className="block text-sm font-medium text-[#2B2D42] mb-2">
              IBAN (International Bank Account Number)
            </label>
            <input
              type="text"
              name="iban"
              value={formData.iban}
              onChange={handleChange}
              placeholder="e.g., GB29HBUK40127612345678"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00B4D8] font-mono"
            />
          </div>

          {/* SWIFT & Routing */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-[#2B2D42] mb-2">
                SWIFT/BIC Code
              </label>
              <input
                type="text"
                name="swift"
                value={formData.swift}
                onChange={handleChange}
                placeholder="e.g., BOFAUS3N"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00B4D8] font-mono"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#2B2D42] mb-2">
                Routing Number
              </label>
              <input
                type="text"
                name="routing"
                value={formData.routing}
                onChange={handleChange}
                placeholder="e.g., 026009593"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00B4D8] font-mono"
              />
            </div>
          </div>

          {/* Currency */}
          <div>
            <label className="block text-sm font-medium text-[#2B2D42] mb-2">
              Currency <span className="text-red-500">*</span>
            </label>
            <select
              name="currency"
              value={formData.currency}
              onChange={handleChange}
              required
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00B4D8]"
            >
              {CURRENCIES.map(cur => (
                <option key={cur} value={cur}>{cur}</option>
              ))}
            </select>
          </div>

          {/* Is Default */}
          <div>
            <label className="flex items-center space-x-2">
              <input
                type="checkbox"
                name="is_default"
                checked={formData.is_default}
                onChange={handleChange}
                className="h-4 w-4 text-[#00B4D8] border-gray-300 rounded focus:ring-[#00B4D8]"
              />
              <span className="text-sm text-[#2B2D42]">
                Set as default bank account for this company
              </span>
            </label>
          </div>

          {/* Actions */}
          <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
            <Button type="button" onClick={onClose} variant="outline" disabled={loading}>
              Cancel
            </Button>
            <Button
              type="submit"
              className="bg-[#00B4D8] hover:bg-[#0096B8]"
              disabled={loading}
            >
              {loading ? (isEdit ? 'Updating...' : 'Creating...') : (isEdit ? 'Update Account' : 'Add Account')}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default BankAccountFormModal;
